import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Chip, Tooltip, IconButton,
  CircularProgress, Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { Button, Card, Dialog as UiDialog, PageHeader } from 'Components/ui';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { useTranslation } from 'react-i18next';
import { useAuthTenant } from 'store/auth';
import { getScannerAccounts, deleteScannerAccount } from 'services/scanner-service';
import { Main as Container } from 'Components/Main';

// ── Styles ────────────────────────────────────────────────────────────────────
const BTN = {
  borderRadius: '8px', padding: '8px 20px', height: '36px',
  textTransform: 'none', fontSize: '0.875rem', fontWeight: 500,
};

const STATUS_COLORS = { active: 'success', inactive: 'default', error: 'error' };
const PROVIDER_LABELS = { aws: 'AWS', azure: 'Azure', gcp: 'GCP' };

const PROVIDER_COLORS = { aws: '#FF9900', azure: '#0078D4', gcp: '#4285F4' };
const PROVIDER_INITIALS = { aws: 'AWS', azure: 'AZ', gcp: 'GCP' };

const ProviderBadge = ({ provider }) => (
  <Box sx={{
    display: 'inline-flex', alignItems: 'center', gap: 0.8,
    px: 1.2, py: 0.4, borderRadius: 1,
    backgroundColor: PROVIDER_COLORS[provider] ? `color-mix(in srgb, ${PROVIDER_COLORS[provider]} 18%, transparent)` : 'var(--vo-mist)',
    border: `1px solid ${PROVIDER_COLORS[provider] ? `color-mix(in srgb, ${PROVIDER_COLORS[provider]} 40%, transparent)` : 'var(--vo-line)'}`,
  }}>
    <Box sx={{
      width: 16, height: 16, borderRadius: 0.5, flexShrink: 0,
      backgroundColor: PROVIDER_COLORS[provider] || 'var(--vo-steel)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Typography sx={{ fontSize: 7, fontWeight: 700, color: 'var(--vo-white)', lineHeight: 1 }}>
        {PROVIDER_INITIALS[provider] || provider?.toUpperCase()}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'var(--vo-ink-2)' }}>
      {PROVIDER_LABELS[provider] || provider}
    </Typography>
  </Box>
);

// ── Component ─────────────────────────────────────────────────────────────────
export const ScannerAccounts = () => {
  const { t } = useTranslation('scanner');
  const selectedTenant = useSelector(useAuthTenant);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const STATUS_LABELS = {
    active: t('scanner:accounts.statusActive'),
    inactive: t('scanner:accounts.statusInactive'),
    error: t('scanner:accounts.statusError'),
  };

  const { data, isLoading } = useQuery(
    ['scannerAccounts', selectedTenant.slug],
    () => getScannerAccounts(selectedTenant.slug),
    { enabled: !!selectedTenant.slug }
  );

  const deleteMutation = useMutation(
    (id) => deleteScannerAccount(id, selectedTenant.slug),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['scannerAccounts']);
        UiDialog.fire({
          title: t('scanner:accounts.deleteSuccess'),
          text: t('scanner:accounts.deleteSuccessText'),
          icon: 'success',
        });
      },
      onError: () => UiDialog.fire({
        title: t('scanner:accounts.deleteError'),
        text: t('scanner:accounts.deleteErrorText'),
        icon: 'error',
      }),
    }
  );

  const handleDelete = async (account) => {
    const confirmed = await UiDialog.confirm({
      title: t('scanner:accounts.deleteTitle'),
      message: t('scanner:accounts.deleteText', { name: account.name }),
      confirmColor: 'var(--vo-danger)',
      confirmLabel: t('scanner:accounts.deleteConfirm'),
      cancelLabel: t('common:buttons.cancel'),
    });
    if (confirmed) deleteMutation.mutate(account._id);
  };

  const accounts = data?.data || [];

  return (
    <Container>
      <PageHeader
        title={t('scanner:accounts.title')}
        segments={[
          { label: t('common:breadcrumbs.analysis') },
          { label: t('scanner:dashboard.title'), route: '/scanner/dashboard' },
          { label: t('scanner:accounts.title') },
        ]}
        actions={
          <Button
            variant="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/scanner/accounts/new')}
            sx={{ ...BTN, whiteSpace: 'nowrap', backgroundColor: 'var(--color-brand)', color: 'var(--vo-white) !important', '&:hover': { backgroundColor: 'var(--color-brand-strong)' } }}
          >
            {t('scanner:accounts.add')}
          </Button>
        }
      />
      <Box sx={{ mt: 3, pb: 6 }}>

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && accounts.length === 0 && (
        <Card elevation={0} sx={{ p: 5, border: '1px solid var(--vo-line)', borderRadius: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CloudOffIcon sx={{ fontSize: 48, color: 'var(--vo-steel)', mb: 1.5 }} />
            <Typography sx={{ fontWeight: 600, color: 'var(--vo-ink)', mb: 0.5 }}>
              {t('scanner:accounts.emptyTitle')}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'var(--vo-slate)', mb: 3 }}>
              {t('scanner:accounts.emptyDesc')}
            </Typography>
            <Button
              variant="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/scanner/accounts/new')}
              sx={{ ...BTN, backgroundColor: 'var(--color-brand)', '&:hover': { backgroundColor: 'var(--color-brand-strong)' } }}
            >
              {t('scanner:accounts.add')}
            </Button>
          </Box>
        </Card>
      )}

      {/* Accounts table */}
      {accounts.length > 0 && (
        <Card elevation={0} sx={{ padding: 0, border: '1px solid var(--vo-line)', borderRadius: 2, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'var(--vo-mist)' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, color: 'var(--vo-slate)', textTransform: 'uppercase', letterSpacing: 0.4, py: 1.5, width: '35%', minWidth: 180 }}>{t('scanner:accounts.colName')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, color: 'var(--vo-slate)', textTransform: 'uppercase', letterSpacing: 0.4, py: 1.5, width: 130 }}>{t('scanner:accounts.colProvider')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, color: 'var(--vo-slate)', textTransform: 'uppercase', letterSpacing: 0.4, py: 1.5, width: 160 }}>{t('scanner:accounts.colAccountId')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, color: 'var(--vo-slate)', textTransform: 'uppercase', letterSpacing: 0.4, py: 1.5, width: 100 }}>{t('scanner:accounts.colStatus')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, color: 'var(--vo-slate)', textTransform: 'uppercase', letterSpacing: 0.4, py: 1.5, width: 60 }} align="center">{t('scanner:accounts.colActions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => (
                <TableRow
                  key={account._id}
                  hover
                  sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                >
                  <TableCell
                    onClick={() => navigate(`/scanner/scans?accountId=${account._id}`)}
                    sx={{ py: 1.5, fontSize: 13, fontWeight: 500, color: 'var(--vo-ink)' }}
                  >
                    {account.name}
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <ProviderBadge provider={account.provider} />
                  </TableCell>
                  <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
                    <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--vo-ink-2)' }}>
                      {account.uid || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={STATUS_LABELS[account.status] || account.status}
                        color={STATUS_COLORS[account.status] || 'default'}
                        size="small"
                      />
                      {account.status === 'error' && (
                        <Tooltip title={t('scanner:accounts.credError')}>
                          <Typography component="span" sx={{ color: 'var(--vo-danger)', fontSize: 14, cursor: 'help' }}>⚠</Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.5 }}>
                    <Tooltip title={t('scanner:accounts.removeTooltip')}>
                      <IconButton size="small" onClick={() => handleDelete(account)} color="error">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      </Box>
    </Container>
  );
};
